import { Col, Row, Label, CardBody } from 'reactstrap';
import { api } from '../../../config';
import logger from '../../../utils/logger';
import { useState, useEffect } from 'react';

const isFobDetail = details =>
  String(details?.incoterm ?? '')
    .trim()
    .toUpperCase() === 'FOB';

// Alpha HPS belum tentu sudah diunggah; tampilkan '-' supaya sel kosong tidak
// terbaca sebagai nilai nol.
const alphaCell = value =>
  value === null || value === undefined || value === '' ? '-' : value;

// Satu entri listData = satu baris, kecuali FOB yang dipecah
// menjadi satu baris per incotermFOBLocations.
const buildIncotermRows = (item, details) => {
  const base = {
    id: item.id,
    quantityInformationId: details?.quantityInformationId ?? null,
    quantityIncotermId: details.quantityIncotermId ?? null,
    quantityLocation2: details.quantityLocation2 ?? null,
    dyed: details.dyed ?? null,
    undyed: details.undyed ?? null,
    dyed2: details.dyed2 ?? null,
    undyed2: details.undyed2 ?? null,
    alpha1: details.alpha1 ?? 0,
    alpha2: details.alpha2 ?? null,
    // Alpha HPS hasil upload parcel. Untuk baris non-FOB nilainya ada di
    // listData (QuantityInformationDetail); baris FOB menimpanya per lokasi
    // di bawah, karena tiap lokasi punya nilainya sendiri.
    alphaDyedHps: details.alphaDyedHps ?? null,
    alphaUndyedHps: details.alphaUndyedHps ?? null,
  };

  if (!isFobDetail(details)) {
    return [
      {
        ...base,
        deliveryBasis: details.deliveryBasis ?? null,
        incoterm: details.incoterm ?? null,
        route: '',
        loadingPort: '',
        parcelSize: details.parcelSize ?? null,
        port1: details.port1 ?? null,
        addAld: details.addAld ?? null,
        parcelSize2: details.parcelSize2 ?? null,
        port2: details.port2 ?? null,
        addAld2: details.addAld2 ?? null,
        parcelSize3: details.parcelSize3 ?? null,
        port3: details.port3 ?? null,
        addAld3: details.addAld3 ?? null,
      },
    ];
  }

  // Kolom Discharge Qty / Port / ALD-ADD sengaja dikosongkan untuk baris FOB.
  const blanks = {
    parcelSize: '',
    port1: '',
    addAld: '',
    parcelSize2: '',
    port2: '',
    addAld2: '',
    parcelSize3: '',
    port3: '',
    addAld3: '',
  };

  const locations = Array.isArray(details.incotermFOBLocations)
    ? details.incotermFOBLocations
    : [];

  // FOB tanpa lokasi: tetap tampilkan satu baris dari listData supaya
  // entrinya tidak hilang diam-diam dari tabel.
  if (!locations.length) {
    return [
      {
        ...base,
        ...blanks,
        deliveryBasis: details.deliveryBasis ?? null,
        incoterm: details.incoterm ?? null,
        route: details.route ?? '',
        loadingPort: '',
      },
    ];
  }

  return locations.map(loc => ({
    ...base,
    ...blanks,
    deliveryBasis: loc.deliveryBasis ?? null,
    incoterm: loc.incoterm ?? null,
    route: loc.route ?? '',
    loadingPort: loc.name ?? '',
    alphaDyedHps: loc.alphaDyedHps ?? null,
    alphaUndyedHps: loc.alphaUndyedHps ?? null,
  }));
};

const ModeDeliveryTableFragment = ({
  procurementId,
  quantityDeliveryTermId,
  token,
}) => {
  const [qtyModeDeliveryIncoterm, setQtyModeDeliveryIncoterm] = useState({
    items: [],
  });
  const [currentPage] = useState(1);
  const [rowsPerPage] = useState(30);

  useEffect(() => {
    // jangan menembak API sebelum seluruh parameter wajibnya tersedia
    if (!procurementId || !quantityDeliveryTermId || !token) return;

    fetchDeliveryDataDetailIncoterm(
      procurementId,
      quantityDeliveryTermId,
      currentPage,
      rowsPerPage,
      token
    );
  }, [procurementId, quantityDeliveryTermId, currentPage, rowsPerPage, token]);

  const fetchDeliveryDataDetailIncoterm = async (
    procurementId,
    quantityDeliveryTermId,
    currentPage,
    rowsPerPage,
    token
  ) => {
    try {
      const response = await fetch(
        `${api.API_URL}/procurement/get-delivery-detail?PageNumber=${currentPage}&PageSize=${rowsPerPage}&procurementId=${procurementId}&quantityDeliveryTermId=${quantityDeliveryTermId}`,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (!response.ok) {
        throw new Error(`HTTP ${response.status} ${response.statusText}`);
      }

      const result = await response.json();
      const groups = Array.isArray(result?.data?.data) ? result.data.data : [];
      const items = groups.flatMap(item =>
        (item.listData || []).flatMap(details =>
          buildIncotermRows(item, details)
        )
      );
      setQtyModeDeliveryIncoterm({ items });
    } catch (error) {
      logger.error('Error fetchDeliveryDataDetailIncoterm:', error);
    }
  };

  return (
    <Col lg={12}>
      <Row>
        <Col sm={12}>
          <div className='mb-2 mt-3'>
            <Label for='product-name-1' className='form-label fw-semibold'>
              Incoterm
            </Label>
            <CardBody>
              <div className='table-card'>
                <table className='table table-hover table-centered align-middle  '>
                  <thead className='table-light'>
                    <tr>
                      <th scope='col' width='6%'>
                        Parcel Size
                      </th>
                      <th scope='col' width='6%'>
                        Incoterm
                      </th>
                      <th scope='col' width='6%'>
                        Route
                      </th>
                      <th scope='col' width='6%'>
                        Loading Port
                      </th>
                      <th scope='col' width='6%'>
                        Discharge Qty 1
                      </th>
                      <th scope='col' width='6%'>
                        Port 1
                      </th>
                      <th scope='col' width='8%'>
                        ALD/ADD
                      </th>
                      <th scope='col' width='6%'>
                        Discharge Qty 2
                      </th>
                      <th scope='col' width='9%'>
                        Port 2
                      </th>
                      <th scope='col' width='8%'>
                        ALD/ADD
                      </th>
                      <th scope='col' width='6%'>
                        Discharge Qty 3
                      </th>
                      <th scope='col' width='9%'>
                        Port 3
                      </th>
                      <th scope='col' width='8%'>
                        ALD/ADD
                      </th>
                      <th scope='col' width='5%'>
                        Alpha Dyed
                      </th>
                      <th scope='col' width='5%'>
                        Alpha UnDyed
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {qtyModeDeliveryIncoterm.items.map((data, index) => (
                      <tr key={`${data.id}-${index}`}>
                        <td>{data.deliveryBasis}</td>
                        <td>{data.incoterm}</td>
                        <td>{data.route}</td>
                        <td>{data.loadingPort}</td>
                        <td>{data.parcelSize}</td>
                        <td>{data.port1}</td>
                        <td>{data.addAld}</td>
                        <td>{data.parcelSize2}</td>
                        <td>{data.port2}</td>
                        <td>{data.addAld2}</td>
                        <td>{data.parcelSize3}</td>
                        <td>{data.port3}</td>
                        <td>{data.addAld3}</td>
                        <td>{alphaCell(data.alphaDyedHps)}</td>
                        <td>{alphaCell(data.alphaUndyedHps)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardBody>
          </div>
        </Col>
      </Row>
    </Col>
  );
};

export default ModeDeliveryTableFragment;
